// Nahrávání hlasovky (SPEC.md 2.9): MediaRecorder nad mikrofonem, časovač, úroveň hlasitosti a limit délky.
import { VOICE_NOTE_MAX_SECONDS } from '@wac-buddy/shared'

/** Klíče chyb (i18n: buddy.voice.errors.*). */
export type RecorderErrorKey = 'notAllowed' | 'notFound' | 'unsupported'

export class RecorderError extends Error {
  constructor(
    readonly key: RecorderErrorKey,
    cause?: unknown,
  ) {
    super(`Nahrávání selhalo: ${key}`)
    this.name = 'RecorderError'
    this.cause = cause
  }
}

/** Pořadí, v jakém zkoušíme formáty: webm/opus (Chrome, Android, Firefox), mp4 (iOS Safari), ogg. */
export const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
] as const

export interface Recording {
  blob: Blob
  /** Typ bez parametrů (`audio/webm`), tak ho server očekává. */
  mime: string
  durationMs: number
}

export interface RecorderOptions {
  /** Každých ~100 ms: kolik ms už se nahrává. */
  onTick?: (elapsedMs: number) => void
  /** Každých ~100 ms: hlasitost 0 až 1 (jen když prohlížeč umí AudioContext). */
  onLevel?: (level: number) => void
  /** Dosažen limit délky: nahrávání se samo zastavilo, výsledek vrátí `stop()`. */
  onLimit?: () => void
}

export interface Recorder {
  isSupported(): boolean
  start(): Promise<void>
  stop(): Promise<Recording>
  /** Zahodí nahrávku a uvolní mikrofon. */
  cancel(): void
}

const TICK_MS = 100

/** Vybere první formát, který prohlížeč umí nahrávat; undefined = nechat na prohlížeči. */
export function pickMimeType(
  isTypeSupported: ((mime: string) => boolean) | undefined = typeof MediaRecorder === 'undefined'
    ? undefined
    : (m) => MediaRecorder.isTypeSupported(m),
): string | undefined {
  if (!isTypeSupported) return undefined
  return MIME_CANDIDATES.find((m) => {
    try {
      return isTypeSupported(m)
    } catch {
      return false
    }
  })
}

/** `audio/webm;codecs=opus` -> `audio/webm`. */
export function stripMime(mime: string): string {
  return (mime.split(';')[0] ?? '').trim().toLowerCase()
}

/** Chyba getUserMedia / MediaRecorder -> klíč pro uživatele. */
export function mapRecorderError(e: unknown): RecorderErrorKey {
  if (e instanceof RecorderError) return e.key
  const name = typeof e === 'object' && e !== null ? String((e as { name?: unknown }).name) : ''
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'notAllowed'
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'NotReadableError':
    case 'OverconstrainedError':
    case 'AbortError':
      return 'notFound'
    default:
      return 'unsupported'
  }
}

type AudioContextCtor = typeof AudioContext

function audioContextCtor(): AudioContextCtor | undefined {
  const g = globalThis as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor }
  return g.AudioContext ?? g.webkitAudioContext
}

export function createRecorder(options: RecorderOptions = {}): Recorder {
  let stream: MediaStream | null = null
  let media: MediaRecorder | null = null
  let audioCtx: AudioContext | null = null
  let analyser: AnalyserNode | null = null
  let samples: Uint8Array<ArrayBuffer> | null = null
  let timer: ReturnType<typeof setInterval> | null = null
  let chunks: Blob[] = []
  let startedAt = 0
  let mimeType: string | undefined
  let stopPromise: Promise<Recording> | null = null

  function isSupported(): boolean {
    return (
      typeof MediaRecorder !== 'undefined' &&
      typeof navigator !== 'undefined' &&
      typeof navigator.mediaDevices?.getUserMedia === 'function'
    )
  }

  function stopTimer(): void {
    if (timer !== null) {
      clearInterval(timer)
      timer = null
    }
  }

  /** Uvolní mikrofon a zvukový kontext (zhasne ukazatel nahrávání v prohlížeči). */
  function release(): void {
    stopTimer()
    stream?.getTracks().forEach((track) => track.stop())
    stream = null
    if (audioCtx) {
      void audioCtx.close().catch(() => undefined)
      audioCtx = null
    }
    analyser = null
    samples = null
  }

  function setupLevel(): void {
    const Ctor = audioContextCtor()
    if (!Ctor || !stream || !options.onLevel) return
    try {
      audioCtx = new Ctor()
      const source = audioCtx.createMediaStreamSource(stream)
      analyser = audioCtx.createAnalyser()
      analyser.fftSize = 512
      source.connect(analyser)
      samples = new Uint8Array(new ArrayBuffer(analyser.fftSize))
    } catch {
      // Úroveň je jen ozdoba, nahrávání jde i bez ní.
      analyser = null
      samples = null
    }
  }

  function readLevel(): number {
    if (!analyser || !samples) return 0
    analyser.getByteTimeDomainData(samples)
    let sum = 0
    for (const s of samples) {
      const v = (s - 128) / 128
      sum += v * v
    }
    return Math.min(1, Math.sqrt(sum / samples.length) * 4)
  }

  function elapsed(): number {
    return Math.max(0, Date.now() - startedAt)
  }

  function tick(): void {
    const ms = elapsed()
    options.onTick?.(ms)
    if (analyser) options.onLevel?.(readLevel())
    if (ms >= VOICE_NOTE_MAX_SECONDS * 1000 && media?.state === 'recording') {
      stop().catch(() => undefined)
      options.onLimit?.()
    }
  }

  async function start(): Promise<void> {
    if (!isSupported() || media) throw new RecorderError('unsupported')
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      mimeType = pickMimeType()
      media = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
    } catch (e) {
      release()
      media = null
      throw new RecorderError(mapRecorderError(e), e)
    }
    chunks = []
    stopPromise = null
    media.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) chunks.push(event.data)
    }
    setupLevel()
    startedAt = Date.now()
    try {
      media.start(1000)
    } catch (e) {
      release()
      media = null
      throw new RecorderError(mapRecorderError(e), e)
    }
    timer = setInterval(tick, TICK_MS)
  }

  function stop(): Promise<Recording> {
    if (stopPromise) return stopPromise
    const recorder = media
    if (!recorder) return Promise.reject(new RecorderError('unsupported'))
    // Automatický stop přijde až při dalším tiku, délka ale nesmí limit překročit.
    const durationMs = Math.min(elapsed(), VOICE_NOTE_MAX_SECONDS * 1000)
    stopPromise = new Promise<Recording>((resolve, reject) => {
      const fail = (e: unknown) => {
        release()
        media = null
        reject(new RecorderError(mapRecorderError(e), e))
      }
      recorder.onstop = () => {
        const mime = stripMime(recorder.mimeType || mimeType || '') || 'audio/webm'
        const blob = new Blob(chunks, { type: mime })
        release()
        media = null
        resolve({ blob, mime, durationMs })
      }
      recorder.onerror = () => fail(undefined)
      stopTimer()
      if (recorder.state === 'inactive') {
        recorder.onstop?.(new Event('stop'))
        return
      }
      try {
        recorder.stop()
      } catch (e) {
        fail(e)
      }
    })
    return stopPromise
  }

  function cancel(): void {
    const recorder = media
    if (recorder) {
      // Posluchače odpojíme, výsledek nikoho nezajímá.
      recorder.ondataavailable = null
      recorder.onstop = null
      recorder.onerror = null
      if (recorder.state !== 'inactive') {
        try {
          recorder.stop()
        } catch {
          /* už je zastavený */
        }
      }
    }
    release()
    media = null
    chunks = []
    stopPromise = null
  }

  return { isSupported, start, stop, cancel }
}
